import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema, generateOrganizationSchema } from '../../utils/seo';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Contact | Voyage Elite - Réservation Transport Premium Genève Lyon Ski',
    description: 'Contactez Voyage Elite pour réserver votre transfert premium. Disponible 24/7 au +33 6 05 60 60 06 ou contac.driver@outlook.fr. Réponse rapide garantie.',
    keywords: 'contact Voyage Elite, réserver transfert ski, téléphone VTC Genève, email transport premium, réservation chauffeur privé',
    canonical: '/contact',
    ogTitle: 'Contactez Voyage Elite - Réservation Transport Premium',
    ogDescription: 'Contactez-nous 24/7 pour réserver votre transfert premium vers les Alpes. Réponse rapide garantie.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/contact#webpage`,
          url: `${siteUrl}/contact`,
          name: 'Contact Voyage Elite',
          description: 'Contactez Voyage Elite pour vos réservations et demandes d\'information',
          inLanguage: 'fr-FR',
          isPartOf: {
            '@id': `${siteUrl}/#website`
          }
        },
        {
          '@type': 'ContactPage',
          '@id': `${siteUrl}/contact#contactpage`,
          url: `${siteUrl}/contact`,
          name: 'Page de Contact Voyage Elite',
          description: 'Formulaire de contact et informations pour nous joindre 24/7',
          inLanguage: 'fr-FR'
        },
        generateOrganizationSchema(),
        {
          '@type': 'ContactPoint',
          '@id': `${siteUrl}/contact#contactpoint`,
          contactType: 'Réservations et Service Client',
          telephone: '+33605606006',
          email: 'contac.driver@outlook.fr',
          availableLanguage: ['French', 'English'],
          areaServed: ['CH', 'FR'],
          hoursAvailable: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            opens: '00:00',
            closes: '23:59'
          }
        }
      ]
    }
  });

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [formRef, formInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: 'information',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField = (name: string, value: string) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'nom':
        if (value.length < 2) {
          newErrors[name] = 'Le nom doit contenir au moins 2 caractères';
        } else {
          delete newErrors[name];
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[name] = 'Email invalide';
        } else {
          delete newErrors[name];
        }
        break;
      case 'telephone':
        if (value.length < 8) {
          newErrors[name] = 'Numéro de téléphone invalide';
        } else {
          delete newErrors[name];
        }
        break;
      case 'message':
        if (value.length < 10) {
          newErrors[name] = 'Le message doit contenir au moins 10 caractères';
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    validateField(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5md31vpl7huldvcihvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          sujet: 'information',
          message: ''
        });
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        setTimeout(() => setShowSuccess(false), 8000);
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Navbar />

      {/* HERO SECTION */}
      <section ref={heroRef} className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/ea7051658a55bb967beb7e1173682b84.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center gap-2 px-5 py-2 bg-alpine-gold/90 backdrop-blur-sm rounded-full mb-6"
          >
            <i className="ri-customer-service-2-line text-night-blue text-xl"></i>
            <span className="font-inter font-bold text-night-blue text-sm">Service Client 24/7</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="font-playfair text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Contactez-nous
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={heroInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="text-slate-100 font-inter text-xl lg:text-2xl max-w-3xl mx-auto mb-10 leading-relaxed"
          >
            Notre équipe est à votre disposition pour répondre à toutes vos questions
          </motion.p>
        </div>
      </section>

      {/* MESSAGE DE SUCCÈS */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="max-w-5xl mx-auto px-6 -mt-16 mb-12 relative z-20"
          >
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center"
                  >
                    <i className="ri-check-line text-4xl"></i>
                  </motion.div>
                  <div>
                    <h3 className="font-playfair text-2xl font-bold mb-1">Message envoyé avec succès !</h3>
                    <p className="text-green-50">Nous vous répondrons dans les plus brefs délais</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-sm leading-relaxed">
                    <strong>Temps de réponse moyen :</strong><br/>
                    • Email : Moins de 2 heures pendant les heures ouvrables<br/>
                    • Téléphone : Réponse immédiate 24/7<br/>
                    • WhatsApp : Réponse en quelques minutes
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CONTACT INFO + FORM */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* INFORMATIONS DE CONTACT */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h2 className="font-playfair text-3xl font-bold text-night-blue mb-6">
                  Nos Coordonnées
                </h2>
                <p className="text-slate-600 font-inter text-lg leading-relaxed">
                  Disponibles 24h/24 et 7j/7 pour répondre à toutes vos demandes de transfert vers les stations de ski.
                </p>
              </div>

              {/* TÉLÉPHONE */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-alpine-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-alpine-gold text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-inter font-bold text-night-blue mb-2">Téléphone</h3>
                    <a href="tel:+33605606006" className="text-slate-600 hover:text-alpine-gold transition-colors font-inter text-lg">
                      +33 6 05 60 60 06
                    </a>
                    <p className="text-slate-500 text-sm mt-1">Disponible 24/7</p>
                  </div>
                </div>
              </div>

              {/* EMAIL */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-alpine-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-alpine-gold text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-inter font-bold text-night-blue mb-2">Email</h3>
                    <a href="mailto:contac.driver@outlook.fr" className="text-slate-600 hover:text-alpine-gold transition-colors font-inter break-all">
                      contac.driver@outlook.fr
                    </a>
                    <p className="text-slate-500 text-sm mt-1">Réponse sous 2h</p>
                  </div>
                </div>
              </div>

              {/* WHATSAPP */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-green-500 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-inter font-bold text-night-blue mb-2">WhatsApp</h3>
                    <a href="https://wa.me/33605606006" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-green-500 transition-colors font-inter">
                      +33 6 05 60 60 06
                    </a>
                    <p className="text-slate-500 text-sm mt-1">Messagerie instantanée</p>
                  </div>
                </div>
              </div>

              {/* LOCALISATION */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-alpine-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-alpine-gold text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-inter font-bold text-night-blue mb-2">Zone de Service</h3>
                    <p className="text-slate-600 font-inter">
                      Genève, Lyon, Chambéry<br/>
                      Toutes les Alpes françaises
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* FORMULAIRE DE CONTACT */}
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, x: 30 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
                <h2 className="font-playfair text-3xl font-bold text-night-blue mb-8">
                  Envoyez-nous un message
                </h2>

                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  {/* NOM */}
                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      onBlur={(e) => validateField('nom', e.target.value)}
                      required
                      className={`w-full px-4 py-3.5 border-2 rounded-xl font-inter focus:outline-none transition-all ${
                        errors.nom 
                          ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' 
                          : 'border-slate-300 focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20'
                      }`}
                      placeholder="Votre nom"
                    />
                    {errors.nom && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.nom}
                      </p>
                    )}
                  </div>

                  {/* EMAIL */}
                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={(e) => validateField('email', e.target.value)}
                      required
                      className={`w-full px-4 py-3.5 border-2 rounded-xl font-inter focus:outline-none transition-all ${
                        errors.email 
                          ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' 
                          : 'border-slate-300 focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20'
                      }`}
                      placeholder="votre@email.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* TÉLÉPHONE */}
                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      onBlur={(e) => validateField('telephone', e.target.value)}
                      required
                      className={`w-full px-4 py-3.5 border-2 rounded-xl font-inter focus:outline-none transition-all ${
                        errors.telephone 
                          ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' 
                          : 'border-slate-300 focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20'
                      }`}
                      placeholder="+33 / +41"
                    />
                    {errors.telephone && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.telephone}
                      </p>
                    )}
                  </div>

                  {/* SUJET */}
                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Sujet *
                    </label>
                    <select
                      name="sujet"
                      value={formData.sujet}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 border-2 border-slate-300 rounded-xl font-inter focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all cursor-pointer bg-white"
                    >
                      <option value="information">Demande d'information</option>
                      <option value="devis">Demande de devis</option>
                      <option value="reservation">Question sur une réservation</option>
                      <option value="reclamation">Réclamation</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  {/* MESSAGE */}
                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={(e) => validateField('message', e.target.value)}
                      required
                      maxLength={500}
                      rows={6}
                      className={`w-full px-4 py-3.5 border-2 rounded-xl font-inter focus:outline-none transition-all resize-none ${
                        errors.message 
                          ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' 
                          : 'border-slate-300 focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20'
                      }`}
                      placeholder="Décrivez votre demande..."
                    />
                    <div className="flex justify-between items-center mt-2">
                      {errors.message && (
                        <p className="text-red-500 text-xs flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {errors.message}
                        </p>
                      )}
                      <p className="text-slate-500 text-sm font-inter ml-auto">
                        {formData.message.length}/500 caractères
                      </p>
                    </div>
                  </div>

                  {/* BOUTON SUBMIT */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting || Object.keys(errors).length > 0}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-night-blue to-slate-900 text-white py-5 rounded-xl font-inter font-semibold text-lg hover:from-alpine-gold hover:to-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 whitespace-nowrap cursor-pointer shadow-xl relative overflow-hidden group"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          Envoyer le message
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-alpine-gold to-amber-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </motion.button>

                  {/* Bouton WhatsApp */}
                  <motion.a
                    href="https://wa.me/33605606006"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-5 rounded-xl font-inter font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-3 shadow-xl"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    Contacter via WhatsApp
                  </motion.a>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ RAPIDE */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl font-bold text-night-blue mb-4">
              Questions Fréquentes
            </h2>
            <p className="text-slate-600 font-inter text-lg">
              Réponses rapides aux questions les plus courantes
            </p>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: 'Quel est le délai de réponse ?',
                a: 'Nous répondons généralement sous 2 heures pendant les heures ouvrables. Pour les urgences, appelez-nous directement au +33 6 05 60 60 06 (disponible 24/7).'
              },
              {
                q: 'Comment obtenir un devis ?',
                a: 'Utilisez notre calculateur de prix en ligne ou contactez-nous directement. Nous vous fournirons un devis détaillé et personnalisé gratuitement.'
              },
              {
                q: 'Puis-je modifier ma réservation ?',
                a: 'Oui, vous pouvez modifier votre réservation jusqu\'à 24h avant le départ sans frais supplémentaires. Contactez-nous par téléphone ou email.'
              },
              {
                q: 'Quels moyens de paiement acceptez-vous ?',
                a: 'Nous acceptons les cartes bancaires, virements, espèces et paiements en ligne sécurisés. Le paiement peut se faire à l\'avance ou à bord.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <h3 className="font-inter font-bold text-night-blue mb-3 flex items-start gap-3">
                  <i className="ri-question-line text-alpine-gold text-xl mt-0.5"></i>
                  {faq.q}
                </h3>
                <p className="text-slate-600 font-inter leading-relaxed pl-8">
                  {faq.a}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
